import { ReactNode, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import FloatingAIAgent from "@/components/FloatingAIAgent";
import { CommandBar } from "@/components/CommandBar";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandOpen((v) => !v);
      }
      if (!isTyping && e.key === "/") {
        e.preventDefault();
        setCommandOpen(true);
      }
      if (e.key === "Escape") setCommandOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar onOpenCommand={() => setCommandOpen(true)} />
        
        <div className="flex-1 flex flex-col">
          {/* Spacing to maintain vertical centering */}
          <div className="h-16 flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="lg:hidden" />
            </div>
            <div className="flex items-center gap-4">
              {/* Empty space for balance */}
            </div>
          </div>

          {/* Main Content with Page Transitions */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.main
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="flex-1 p-6"
            >
              {children}
            </motion.main>
          </AnimatePresence>
          {location.pathname !== '/ai-agent' && <FloatingAIAgent />}
        </div>
      </div>
      <CommandBar open={commandOpen} onClose={() => setCommandOpen(false)} />
    </SidebarProvider>
  );
}
